// React
import { useState, useRef, useCallback } from 'react';

// Interfaces
import { IUsePaginationProps } from './interfaces';
import { IBasePaginationBody } from 'interfaces/response';
import { ITableEventAttrs } from 'interfaces/table';

// Utils
import { tableUtils_handleTableChange } from 'utils/common/table.utils';

const initialValuePagination: IBasePaginationBody = {
  pageNumber: 0, //  set initial value, 0 mean 1, 1 mean 0
  sortDirection: true,
  sortColumn: 'Id',
  searchKeyword: '',
  itemsPerPage: 10,
};

const usePagination = ({ lastElementLoading, lastElementCallback }: IUsePaginationProps) => {
  // Hook
  const [pagination, setPagination] = useState<IBasePaginationBody>(initialValuePagination);

  // Check if user has arrive in the last element / data in the screen
  const observer = useRef<any>();

  /**
   * @description Take the last element, commonly use for infinite scroll
   *
   * @param {any} node
   *
   * @return {void} void
   */
  const pagination_lastElement = useCallback(
    node => {
      if (lastElementLoading) return;
      if (observer.current) observer.current.disconnect();
      observer.current = new IntersectionObserver(entries => {
        // If user reaching the last element
        if (entries[0].isIntersecting) {
          lastElementCallback && lastElementCallback(1);
        }
      });
      if (node) observer.current.observe(node);
    },

    // eslint-disable-next-line
    [lastElementLoading],
  );

  /**
   * @description Watch any change in pagination
   *
   * @param {ITableEventAttrs} event
   *
   * @return {void} void
   */
  const pagination_onChange = useCallback(({ name, value }: ITableEventAttrs): void => {
    if (name === 'reset') {
      setPagination(() => ({ ...initialValuePagination, pageNumber: 0 }));
    } else {
      setPagination(prevPagination => ({
        ...prevPagination,
        ...tableUtils_handleTableChange(prevPagination, name, value),
        pageNumber: name !== 'pageNumber' ? 0 : value,
      }));
    }
  }, []);

  return { pagination, setPagination, pagination_lastElement, pagination_onChange };
};

export { usePagination };
